import { useState } from "react";
import { Link } from "react-router-dom";
import { LinkedinLogo, XLogo, YoutubeLogo, TelegramLogo, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import { openCookieBanner } from "@/components/CookieConsentBanner";
import { API_BASE_URL } from "@/lib/api";

const footerLinks = {
  services: {
    title: "Services",
    links: [
      { label: "For Individuals", href: "/individuals" },
      { label: "For Businesses", href: "/businesses" },
    ],
  },
  company: {
    title: "Company",
    links: [
      { label: "About", href: "/about" },
      { label: "Blog", href: "/blog" },
      { label: "Contact", href: "/contact" },
    ],
  },
  individual: {
    title: "Individual Services",
    links: [
      { label: "Investing & Trading", href: "/services/investing-trading" },
      { label: "Financial Planning", href: "/services/financial-planning" },
      { label: "Tax Planning", href: "/services/tax-planning" },
      { label: "Portfolio Management", href: "/services/portfolio-management" },
      { label: "Security & Custody", href: "/services/security-custody" },
    ],
  },
  business: {
    title: "Business Services",
    links: [
      { label: "Licensing & Compliance", href: "/services/licensing-compliance" },
      { label: "Hiring & Team Building", href: "/services/hiring-team-building" },
      { label: "Development", href: "/services/development" },
      { label: "Project Management", href: "/services/project-management" },
      { label: "Tokenization & Launch", href: "/services/tokenization-launch" },
    ],
  },
  tools: {
    title: "Free Calculators",
    links: [
      { label: "All Calculators", href: "/tools" },
      { label: "Crypto Tax Calculator", href: "/calculators/crypto-tax-calculator-india" },
      { label: "TDS Calculator", href: "/calculators/crypto-tds-calculator" },
      { label: "Profit/Loss Calculator", href: "/calculators/crypto-profit-loss-calculator" },
      { label: "Staking Rewards Calculator", href: "/calculators/crypto-staking-rewards-calculator" },
      { label: "Crypto SIP Calculator", href: "/calculators/crypto-sip-calculator" },
    ],
  },
};

const socialLinks = [
  { icon: LinkedinLogo, href: "#", label: "LinkedIn" },
  { icon: XLogo, href: "#", label: "X" },
  { icon: YoutubeLogo, href: "#", label: "YouTube" },
  { icon: TelegramLogo, href: "#", label: "Telegram" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const validSocialLinks = socialLinks.filter((social) => social.href && social.href !== "#");
  const legalLinks = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms & Conditions", href: "/terms-and-conditions" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/newsletter`, { email });
      setSubmitted(true);
      setEmail("");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#21324D] text-[#F5F5F2]" data-testid="site-footer">
      {/* Newsletter Section */}
      <div className="border-b border-[#F5F5F2]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold mb-3">
                Stay ahead in crypto
              </h3>
              <p className="font-body text-[#F5F5F2]/70 text-base">
                Expert insights on crypto regulation, tax planning, and investment strategy. Delivered to your inbox.
              </p>
            </div>
            <div>
              {submitted ? (
                <p className="font-body text-[#A7A55C] text-base" data-testid="newsletter-success">
                  Thank you for subscribing. We'll be in touch.
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="flex gap-0" data-testid="newsletter-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="flex-1 bg-[#F5F5F2]/10 border border-[#F5F5F2]/20 text-[#F5F5F2] placeholder:text-[#F5F5F2]/40 px-5 py-3.5 font-body text-sm focus:outline-none focus:border-[#A7A55C] transition-colors"
                    required
                    data-testid="newsletter-email-input"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#A7A55C] hover:bg-[#8C8A4A] text-[#F5F5F2] px-6 py-3.5 font-body font-semibold text-sm transition-colors flex items-center gap-2"
                    data-testid="newsletter-submit-button"
                  >
                    Subscribe <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Links Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10">
          {Object.values(footerLinks).map((section) => (
            <div key={section.title}>
              <h4 className="font-body font-semibold text-sm uppercase tracking-[0.15em] text-[#F5F5F2]/50 mb-6">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="font-body text-sm text-[#F5F5F2]/70 hover:text-[#A7A55C] transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#F5F5F2]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <img src="/logo-white.svg" alt="BitGenix" className="h-7 w-fit" />
              <span className="font-body text-xs text-[#F5F5F2]/40">
                &copy; {new Date().getFullYear()} BitGenix. All rights reserved.
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-body text-xs text-[#F5F5F2]/55 hover:text-[#A7A55C] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={openCookieBanner}
                className="font-body text-xs text-[#F5F5F2]/55 hover:text-[#A7A55C] transition-colors"
              >
                Cookie Settings
              </button>
            </div>
          </div>

          <p className="font-body text-xs text-[#F5F5F2]/45 leading-relaxed mt-6 max-w-4xl">
            Disclaimer: The information on this website is provided for general informational purposes only and does not constitute legal, tax, investment, financial, or regulatory advice. You should obtain independent professional advice before acting on anything published on this website.
          </p>

          {validSocialLinks.length > 0 && (
            <div className="flex items-center gap-4 mt-6">
              {validSocialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="text-[#F5F5F2]/50 hover:text-[#A7A55C] transition-colors duration-200"
                  aria-label={social.label}
                  rel="noopener noreferrer"
                  target="_blank"
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon size={20} weight="regular" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
